import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router';
import MobilityList from '../common/BakeasyList';
import {
  Logout,
  Store,
  Inventory,
  Category,
  SettingsOutlined,
  FormatListBulleted,
  Assessment,
  Message,
  Translate,
  LocalShipping,
  Receipt,
  RotateLeft,
} from '@mui/icons-material';
import { Card, Grid, List, Typography, useMediaQuery } from '@mui/material';
import orderezlogo from '../../assets/orderezlogo.jpg';
import { clearCookies } from '../../utils/CookieUtils';
import i18n from '../../languages/languages';
import { BakeasySelectLabelProfile } from '../dropdowns/BakeasySelect';
import { t } from 'i18next';
import BakeasyList from '../common/BakeasyList';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { authActions } from '../../redux_store/slices/authSlice';
import { clearCategoryState } from '../../redux_store/slices/categorySlice';
import { clearProductState } from '../../redux_store/slices/productsSlice';
import { clearStoreState } from '../../redux_store/slices/storesSlice';
import {
  clearOrdersState,
  fetchNewOrderCount,
} from '../../redux_store/slices/ordersSlice';
import { BLUE_GRAY_500 } from '../../constants/Colors';
import { AppContext } from '../../context/AppContext';
import { modifyLanguage } from '../../redux_store/slices/languagesSlice';
import { clearSubCategoryState } from '../../redux_store/slices/subCategorySlice';
import {
  getProfile,
  getProfileState,
} from '../../redux_store/slices/GetUserProfileSlice';
import {
  clearMessagesState,
  fetchNewMessageCount,
} from '../../redux_store/slices/messagesSlice';
import { findLanguageCode } from '../../languages/LanguageUtils';
import { clearProductionPlanState } from '../../redux_store/slices/productionPlanSlice';
import { clearProductionReportState } from '../../redux_store/slices/productionReportSlice';
import { clearShippingsState } from '../../redux_store/slices/shippingsSlice';
import { clearInvoiceState } from '../../redux_store/slices/invoiceSlice';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` },
});

const FireNav = styled(List)<{ component?: React.ElementType }>({
  '& .MuiListItemIcon-root': { minWidth: 0 },
  '& .MuiSvgIcon-root': { fontSize: 20 },
});

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

export const DrawerHeaderIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      ...(isMobile && {
        position: 'fixed',
        zIndex: theme.zIndex.modal + 1,
        ...(open && { width: '100%' }),
      }),
    }),
  };
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(isMobile && {
      position: 'fixed',
      zIndex: theme.zIndex.modal + 1,
      marginTop: theme.spacing(8),
      ...(open && { width: '100%' }),
      ...(!open && { display: 'none' }),
    }),
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': { ...openedMixin(theme) },
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  };
});

export interface MenuItemProps {
  id: number;
  lable: string;
  icon: any;
  isSelected: boolean;
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}

interface BakeasyNavigationDrawer {
  onDrawerStateChange?: (open: boolean) => void;
}

export default function BakeasyNavigationDrawer({
  onDrawerStateChange,
}: BakeasyNavigationDrawer) {
  // Close the drawer only in mobile view
  const handleMenuItemClick = (route: string) => {
    navigate(route);

    if (window.innerWidth <= theme.breakpoints.values.sm) {
      handleDrawerClose();
    }
  };

  const menuItemsList: MenuItemProps[] = [
    {
      id: 1,
      lable: 'STORES',
      icon: <Store />,
      isSelected: false,
      onClick: async (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearStoreState());
        handleMenuItemClick('/stores');
      },
    },
    {
      id: 2,
      lable: 'CATEGORIES',
      isSelected: false,
      icon: <Category />,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearCategoryState());
        dispatch(clearSubCategoryState());
        dispatch(clearProductState());
        handleMenuItemClick('/categories');
      },
    },
    {
      id: 3,
      lable: 'PRODUCTS',
      isSelected: false,
      icon: <Inventory />,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearProductState());
        handleMenuItemClick('/products');
      },
    },
    {
      id: 4,
      lable: 'ORDERS',
      icon: <FormatListBulleted />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearOrdersState());
        handleMenuItemClick('/orders');
      },
    },
    {
      id: 5,
      lable: 'ACTIVE_ORDERS',
      icon: <RotateLeft />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearOrdersState());
        handleMenuItemClick('/activeOrders');
      },
    },
    {
      id: 6,
      lable: 'PRODUCTION_PLAN',
      icon: <Assessment />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearProductionPlanState());
        handleMenuItemClick('/productionPlan');
      },
    },
    {
      id: 7,
      lable: 'PRODUCTION_REPORT_HISTORY',
      icon: <Assessment />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearProductionReportState());
        handleMenuItemClick('/productionReportHistory');
      },
    },
    {
      id: 8,
      lable: 'SHIPPING_MEMOS',
      icon: <LocalShipping />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearShippingsState());
        handleMenuItemClick('/shippingMemos');
      },
    },
    {
      id: 9,
      lable: 'INVOICES',
      icon: <Receipt />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(fetchNewOrderCount(''));
        dispatch(clearInvoiceState());
        handleMenuItemClick('/invoices');
      },
    },
    {
      id: 10,
      lable: 'MESSAGES',
      icon: <Message />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(clearMessagesState());
        dispatch(fetchNewOrderCount(''));
        dispatch(fetchNewMessageCount(''));
        handleMenuItemClick('/messages');
      },
    },
  ];

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(!isMobile);
  const navigate = useNavigate();
  const [menus, setMenus] = React.useState(menuItemsList);
  const [toolbarOpen, setToolbarOpen] = React.useState(false);
  const [assignedRoles, setAssignedRoles] = React.useState('');

  const [defaultLanguage, setDefaultLanguage] = React.useState('');
  const [logoState, setLogoState] = React.useState<string>('');

  let myContext = React.useContext(AppContext);
  let _userProfile: any = useAppSelector(getProfileState);

  const handleDrawerOpen = () => {
    setOpen(true);
    onDrawerStateChange?.(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    onDrawerStateChange?.(false);
  };

  const LogOut = () => {
    clearCookies();
    return dispatch(authActions.logout());
  };

  function getAndSetMenus() {
    let menuList: MenuItemProps[] = menuItemsList;
    let roles: string = t('BAKEASY_SUPPLIER_PORTAL');
    setMenus(menuList);
    setAssignedRoles(roles);
  }

  function changeLanguage(language: any) {
    setDefaultLanguage(language);
    window.location.reload();
  }

  React.useEffect(() => {
    if (_userProfile) {
      setLogoState(_userProfile?.value?.logo);
    }
  }, [_userProfile]);

  function handleLogoError() {
    setLogoState(orderezlogo);
  }

  React.useEffect(() => {
    let temp = _userProfile?.value?.defaultLanguage;
    i18n.changeLanguage(findLanguageCode(temp));
    getAndSetMenus();
  }, [_userProfile, defaultLanguage]);

  React.useEffect(() => {
    function handleResize() {
      if (window.innerWidth <= theme.breakpoints.values.sm) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    }

    window.addEventListener('resize', handleResize);

    // Cleanup function
    return () => window.removeEventListener('resize', handleResize);
  }, [theme.breakpoints.values.sm]);

  const profileMenuList: MenuItemProps[] = [
    {
      id: 1,
      lable: 'PROFILE',
      isSelected: false,
      icon: (
        <SettingsOutlined
          sx={{ fontSize: '1.25rem', marginRight: 1, color: BLUE_GRAY_500 }}
        />
      ),
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        handleMenuItemClick('/profile');
      },
    },
    {
      id: 2,
      lable: 'LANGUAGE',
      icon: (
        <Translate
          sx={{ fontSize: '1.25rem', marginRight: 1, color: BLUE_GRAY_500 }}
        />
      ),
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        openLanguageConfirmDialog();
      },
    },
    {
      id: 3,
      lable: 'LOGOUT',
      icon: (
        <Logout
          sx={{ fontSize: '1.25rem', marginRight: 1, color: BLUE_GRAY_500 }}
        />
      ),
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => LogOut(),
    },
  ];

  function openLanguageConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'language-Dialog',
      open: true,
      title: t('SELECT_LANGUAGE'),
      onYesClick: async (e: any, newLanguage) => {
        await dispatch(modifyLanguage({ language: newLanguage }));
        await dispatch(getProfile(''));
        changeLanguage(newLanguage);
      },
    });
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar
          style={{ backgroundColor: 'white', justifyContent: 'space-between' }}
        >
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            edge="start"
            sx={{ marginRight: 5, fontSize: 20 }}
          >
            {<MenuIcon />}
          </IconButton>
          <Typography sx={{ flexGrow: 1 }}>{assignedRoles}</Typography>

          <Grid>
            <BakeasySelectLabelProfile
              options={profileMenuList}
              value={''}
              onChange={(event: any) => { }}
            />
          </Grid>
        </Toolbar>
        {toolbarOpen && (
          <Card sx={{ position: 'absolute', right: '25px', top: '50px' }}>
            <BakeasyList
              items={profileMenuList}
              dense={true}
              onMouseOver={(event: React.MouseEvent<HTMLDivElement>) => {
                setToolbarOpen(true);
              }}
              onMouseOut={(event: React.MouseEvent<HTMLDivElement>) => {
                setToolbarOpen(false);
              }}
            />
          </Card>
        )}
      </AppBar>
      <Drawer
        variant={isMobile && !open ? 'temporary' : 'permanent'}
        open={isMobile || open}
        onClose={handleDrawerClose}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          ...(isMobile && {
            position: 'fixed',
            zIndex: theme.zIndex.drawer + 1,
            marginTop: 90,
            top: 90,
            bottom: 0,
          }),
          ...(!isMobile && { marginTop: theme.mixins.toolbar.minHeight }),
        }}
      >
        {isMobile ? (
          <Box component="main" sx={{ position: 'absolute' }}>
            <DrawerHeader>
              <img
                src={logoState}
                height="60px"
                style={{ cursor: 'pointer' }}
                // crossOrigin="anonymous"
                onError={handleLogoError}
              />
            </DrawerHeader>
          </Box>
        ) : (
          <DrawerHeader>
            <img
              src={
                _userProfile?.value?.logo
                  ? _userProfile?.value?.logo
                  : orderezlogo
              }
              height="60px"
              style={{ cursor: 'pointer' }}
            //crossOrigin="anonymous"
            />
          </DrawerHeader>
        )}
        <Divider />
        <FireNav
          component="nav"
          sx={isMobile ? { p: '12px', marginTop: 6 } : {}}
        >
          <MobilityList items={menus} open={open} />
        </FireNav>
      </Drawer>
    </Box>
  );
}
